import { useEffect } from "react";
import { api } from "../lib/api";
import { PUSH_OPENED_EVENT, alertFromPush, alertsFromFeed, raiseOrderAlert } from "../lib/orderAlert";
import { onWebPushMessage } from "../lib/webPush";

export const ORDER_ALERT_FEED_MS = 8000;

/**
 * Brings persistent order alerts to consoles that have no FCM listener — the web admin and merchant consoles.
 *
 * Two sources, deduped by OrderAlertHost on the alert key, so an order never alerts twice:
 *
 *  1. **Web push**, forwarded by the service worker (lib/webPush.js). This is the real-time path and the only one
 *     that survives a background tab — browsers throttle timers in hidden tabs to roughly once a minute and stop
 *     them entirely in a discarded one, so polling alone meant a minimised browser learned about a new order
 *     minutes late or not at all.
 *  2. **Polling** ``GET /push/my-order-alerts``, which reads the same push log the phone push was sent from and
 *     applies the same rule server-side. It stays as the fallback for browsers with no push support, no VAPID
 *     keys configured, or notifications declined — the console keeps alerting while its tab is open.
 */
export default function OrderAlertFeed() {
    useEffect(() => {
        let cursor = null;
        let stopped = false;
        let busy = false;
        const poll = async () => {
            if (busy) return;
            busy = true;
            try {
                const { data } = await api.get("/push/my-order-alerts", { params: cursor ? { since: cursor } : {} });
                if (stopped) return;
                if (data?.cursor) cursor = data.cursor;
                alertsFromFeed(data?.alerts).forEach(raiseOrderAlert);
            } catch { /* offline or signed out — try again next tick */ } finally {
                busy = false;
            }
        };
        poll();
        const id = setInterval(poll, ORDER_ALERT_FEED_MS);

        const offPush = onWebPushMessage((payload) => {
            if (stopped) return;
            const alert = alertFromPush(payload);
            if (!alert) return;
            // Clicking the OS notification opens the order, which acknowledges its alert instead of raising one.
            if (payload?.opened) {
                try { window.dispatchEvent(new CustomEvent(PUSH_OPENED_EVENT, { detail: payload })); } catch { /* ignore */ }
                return;
            }
            raiseOrderAlert(alert);
        });

        return () => { stopped = true; clearInterval(id); offPush(); };
    }, []);
    return null;
}
